import React, { useContext, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Flex, Image, Link, Spinner, Text, VStack } from '@chakra-ui/react';
import { UserContext } from '../UserContext';
import { authDestination, authReturn } from '../client/authContinuation';
import { authPost, completeBrowserSession } from './socialAuth';
import { providerName } from './ProviderButtons';
import '../App.css';

const errors = {
  cancelled: 'Sign-in was cancelled. You can try again or use email.',
  provider: 'The provider could not complete sign-in. Try again or use email.',
  expired: 'This sign-in has expired. Start again to continue.',
};

export default function AuthComplete() {
  const query = new URLSearchParams(window.location.search);
  const request = query.get('request');
  const initialError = errors[query.get('error')] || (!/^[A-Za-z0-9_-]{43}$/.test(request || '') ? errors.expired : '');
  const [error, setError] = useState(initialError);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(!initialError);
  const { setUserDetails } = useContext(UserContext);
  const navigate = useNavigate();
  const completion = `/auth/complete?request=${request}`;
  const previous = authReturn();
  const returnTo = result?.return_to || (previous?.startsWith('/auth/complete?') ? null : previous);
  const signIn = authDestination('/login', returnTo);

  useEffect(() => {
    if (initialError) return;
    let cancelled = false;
    (async () => {
      try {
        const value = await authPost('result', { request });
        if (cancelled) return;
        if (value.status === 'ready' || value.status === 'signup') {
          const session = await authPost('complete', { request });
          if (!cancelled) {
            sessionStorage.removeItem('pabs-signup-email');
            await completeBrowserSession(session.token, session.return_to, setUserDetails, navigate);
          }
          return;
        }
        setResult(value);
      } catch (failure) { if (!cancelled) setError(failure.message); }
      finally { if (!cancelled) setBusy(false); }
    })();
    return () => { cancelled = true; };
    // The request, rather than an unstable context callback, owns this exchange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  const finish = async event => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const session = await authPost('complete', { request });
      await completeBrowserSession(session.token, session.return_to, setUserDetails, navigate);
    } catch (failure) { setError(failure.message); setBusy(false); }
  };
  const name = result ? providerName(result.provider) : '';

  return (
    <Flex className="start-container" direction={{ base: 'column', md: 'row' }}>
      <Box flex="1.5" minW={0} pr={{ base: 0, md: '5%' }}>
        <Link as={RouterLink} to="/"><Image src="/images/pablo_logo1.0.svg" alt="Pabs home" boxSize="48px" mb="5%" /></Link>
        <Text as="h1" fontSize={{ base: '4xl', md: '6xl' }} fontWeight="bold" letterSpacing="tight" color="brand.900" lineHeight={1.1} overflowWrap="anywhere">
          {result?.status === 'link' ? `Connect ${name} to Pabs` : 'Continue to Pabs'}
        </Text>
        {result?.email && <Text mt={4} fontSize="lg" color="gray.700" overflowWrap="anywhere">{result.email}</Text>}
      </Box>
      <Box className="container-box" minW={0} flex={{ base: 'none', md: '1' }} mt={{ base: 6, md: '5%' }}>
        <VStack align="stretch" spacing={5} p="5%">
          {error && <Alert status="error" role="alert">{error}</Alert>}
          {busy && !result && !error && <Box role="status"><Spinner size="sm" mr={3} /><Text as="span">Finishing sign-in…</Text></Box>}
          {result?.status === 'link' && <>
            <Text>{result.can_link ? `Connect ${name} to ${result.account_email} and continue.`
              : result.unfinished ? 'Finish your email signup first, then connect this login method.'
                : 'You already have a Pabs account with this email. Sign in with your existing method to connect it.'}</Text>
            {result.can_link
              ? <Button colorScheme="brand" isLoading={busy} onClick={finish}>Connect {name} and continue</Button>
              : <Button as={RouterLink} colorScheme="brand" to={`${authDestination(result.unfinished ? '/authorization' : '/login', completion)}${result.unfinished ? '' : `&linking=${result.provider}`}`}
                onClick={() => { if (result.unfinished) sessionStorage.setItem('pabs-signup-email', result.email); }}>
                {result.unfinished ? 'Finish email signup' : 'Sign in to connect'}
              </Button>}
          </>}
          {result?.status === 'email_required' && <>
            <Text>Use email signup to verify this email first. You can then connect {name} from Account Settings.</Text>
            <Button as={RouterLink} colorScheme="brand" to={authDestination('/register', returnTo)}>Continue with email</Button>
          </>}
          {!busy && error && !result && <Button as={RouterLink} colorScheme="brand" to={signIn}>Back to sign in</Button>}
          {result && <Link as={RouterLink} to={signIn} fontSize="sm">Use a different sign-in method</Link>}
        </VStack>
      </Box>
    </Flex>
  );
}
